import { useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import {
  useAuthSession,
  useAuthUser,
} from "@/components/providers/auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { useIsNewAccount } from "@/lib/hooks/use-is-new-account";
import { useSignOut } from "@/lib/hooks/use-sign-out";
import {
  AccountGreeting,
  OnboardingHeader,
  ProgressIndicator,
} from "@/components/onboarding";
import { FullScreenLoader } from "@/components/ui/loader";
import { useOnboardingStore } from "@/lib/stores/onboarding-store";
import { ONBOARDING_STEPS } from "./steps";

export function OnboardingLayout() {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { completedSteps, completeStep } = useOnboardingStore();
  const isNewAccount = useIsNewAccount(profile);
  const signOut = useSignOut();

  // Sync completed steps from backend state
  // This handles the case where localStorage is cleared but backend has state
  useEffect(() => {
    if (!profile?.tenant) return;

    // If org setup is complete on backend but not in local state, sync it
    if (profile.tenant.isOrgSetupComplete && !completedSteps.includes("org")) {
      completeStep("org");
    }
  }, [profile, completedSteps, completeStep]);

  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isProvisioning) {
    return <FullScreenLoader />;
  }

  if (provisioningError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!isProvisioned) {
    return <FullScreenLoader />;
  }

  return (
    <div className="min-h-screen bg-background">
      <OnboardingHeader />
      <main className="container mx-auto max-w-2xl px-4 py-8">
        {profile && (
          <div className="mb-6">
            <AccountGreeting
              isNewAccount={isNewAccount}
              email={profile.user.email}
              onSignOut={() => void signOut()}
            />
          </div>
        )}
        <ProgressIndicator steps={ONBOARDING_STEPS} />
        <div className="mt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
